/**
 * WarmupSeedManager.js
 *
 * Admin-only component for managing SalesGod-owned "seed" email accounts.
 * These accounts are permanently in the warmup pool so every user has
 * real peers from day one — invisible to end users.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebaseConfig';
import { encryptEmailPassword } from '../utils/encryption';
import './WarmupSeedManager.css';

const functions = getFunctions(app, 'europe-west1');

function timeAgoShort(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const PRESETS = {
  gmail: { imapHost: 'imap.gmail.com', imapPort: '993', smtpHost: 'smtp.gmail.com', smtpPort: '465', encryption: 'SSL', smtpEncryption: 'true' },
  outlook: { imapHost: 'outlook.office365.com', imapPort: '993', smtpHost: 'smtp-mail.outlook.com', smtpPort: '587', encryption: 'TLS', smtpEncryption: 'true' },
  hostinger: { imapHost: 'imap.hostinger.com', imapPort: '993', smtpHost: 'smtp.hostinger.com', smtpPort: '465', encryption: 'SSL', smtpEncryption: 'true' },
  custom: { imapHost: 'imap.hostinger.com', imapPort: '993', smtpHost: 'smtp.hostinger.com', smtpPort: '465', encryption: 'SSL', smtpEncryption: 'true' },
};

function WarmupSeedManager() {
  const [seeds, setSeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingEmail, setTogglingEmail] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [preset, setPreset] = useState('custom');
  const [expandedErrorEmail, setExpandedErrorEmail] = useState(null);

  const [form, setForm] = useState({
    email: '',
    password: '',
    senderName: '',
    label: '',
    imapHost: 'imap.hostinger.com',
    imapPort: '993',
    smtpHost: 'smtp.hostinger.com',
    smtpPort: '465',
    encryption: 'SSL',
    smtpEncryption: 'true',
  });

  const fetchSeeds = useCallback(async () => {
    try {
      setLoading(true);
      const fn = httpsCallable(functions, 'listSeedAccounts');
      const result = await fn();
      setSeeds(result.data.seeds || []);
    } catch (err) {
      setError(err.message || 'Failed to load seed accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSeeds(); }, [fetchSeeds]);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS[key];
    if (p) setForm(prev => ({ ...prev, ...p }));
  };

  const handleAdd = async () => {
    if (!form.email || !form.password) {
      setError('Email and password are required');
      return;
    }
    if (!form.imapHost || !form.smtpHost) {
      setError('IMAP and SMTP host are required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const encryptedPw = await encryptEmailPassword(form.password);

      const fn = httpsCallable(functions, 'addSeedAccount');
      await fn({
        email: form.email,
        password: encryptedPw,
        senderName: form.senderName,
        imapHost: form.imapHost,
        imapPort: form.imapPort,
        smtpHost: form.smtpHost,
        smtpPort: form.smtpPort,
        encryptionType: form.encryption,
        smtpEncryption: form.smtpEncryption,
        label: form.label,
      });

      setSuccess(`${form.email} added to warmup network`);
      resetForm();
      setShowForm(false);
      await fetchSeeds();
    } catch (err) {
      setError(err.message || 'Failed to add seed account');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from warmup network?`)) return;
    try {
      const fn = httpsCallable(functions, 'removeSeedAccount');
      await fn({ email });
      setSuccess(`${email} removed`);
      await fetchSeeds();
    } catch (err) {
      setError(err.message || 'Failed to remove');
    }
  };

  const handleToggle = async (email, currentActive) => {
    if (togglingEmail) return;
    setTogglingEmail(email);
    try {
      const fn = httpsCallable(functions, 'toggleSeedAccount');
      await fn({ email, active: !currentActive });
      setSeeds(prev => prev.map(s => s.email === email ? { ...s, active: !currentActive } : s));
    } catch (err) {
      setError(err.message || 'Failed to toggle');
      await fetchSeeds();
    } finally {
      setTogglingEmail(null);
    }
  };

  const resetForm = () => {
    setForm({ email: '', password: '', senderName: '', label: '', imapHost: 'imap.hostinger.com', imapPort: '993', smtpHost: 'smtp.hostinger.com', smtpPort: '465', encryption: 'SSL', smtpEncryption: 'true' });
    setPreset('custom');
  };

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(''), 4000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(''), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  return (
    <div className="wsm_container">
      <div className="wsm_header">
        <h2 className="wsm_title">
          <span className="wsm_title_icon">🔥</span>
          Warmup Seed Accounts
        </h2>
        <p className="wsm_subtitle">
          SalesGod-owned email accounts that are always in the warmup network.
          Users never see them, but they provide real peers from day one.
        </p>
      </div>

      {error && <div className="wsm_alert wsm_alert_error">{error}</div>}
      {success && <div className="wsm_alert wsm_alert_success">{success}</div>}

      <div className="wsm_stats_row">
        <div className="wsm_stat_card">
          <div className="wsm_stat_value">{seeds.length}</div>
          <div className="wsm_stat_label">Seed accounts</div>
        </div>
        <div className="wsm_stat_card">
          <div className="wsm_stat_value">{seeds.filter(s => s.active).length}</div>
          <div className="wsm_stat_label">Active</div>
        </div>
        <div className="wsm_stat_card wsm_stat_card_cta">
          <button className="wsm_add_btn" onClick={() => { if (showForm) resetForm(); setShowForm(!showForm); }}>
            {showForm ? 'Cancel' : '+ Add Account'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="wsm_form_card">
          <h3 className="wsm_form_title">Add Seed Account</h3>

          <div className="wsm_preset_row">
            {['gmail', 'outlook', 'hostinger', 'custom'].map(key => (
              <button
                key={key}
                className={`wsm_preset_btn ${preset === key ? 'wsm_preset_active' : ''}`}
                onClick={() => applyPreset(key)}
              >
                {key === 'gmail' ? 'Gmail' : key === 'outlook' ? 'Outlook' : key === 'hostinger' ? 'Hostinger' : 'Custom'}
              </button>
            ))}
          </div>

          <div className="wsm_form_grid">
            <div className="wsm_field wsm_field_wide">
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="seed@salesgod.io"
              />
            </div>
            <div className="wsm_field wsm_field_wide">
              <label>App Password</label>
              <input
                type="password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                placeholder="App-specific password"
              />
            </div>
            <div className="wsm_field wsm_field_wide">
              <label>Sender Name</label>
              <input
                type="text"
                value={form.senderName}
                onChange={e => setForm(p => ({ ...p, senderName: e.target.value }))}
                placeholder="e.g. Marius Weber"
              />
            </div>
            <div className="wsm_field">
              <label>IMAP Host</label>
              <input
                type="text"
                value={form.imapHost}
                onChange={e => setForm(p => ({ ...p, imapHost: e.target.value }))}
                placeholder="imap.example.com"
              />
            </div>
            <div className="wsm_field">
              <label>IMAP Port</label>
              <input
                type="text"
                value={form.imapPort}
                onChange={e => setForm(p => ({ ...p, imapPort: e.target.value }))}
              />
            </div>
            <div className="wsm_field">
              <label>SMTP Host</label>
              <input
                type="text"
                value={form.smtpHost}
                onChange={e => setForm(p => ({ ...p, smtpHost: e.target.value }))}
                placeholder="smtp.example.com"
              />
            </div>
            <div className="wsm_field">
              <label>SMTP Port</label>
              <input
                type="text"
                value={form.smtpPort}
                onChange={e => setForm(p => ({ ...p, smtpPort: e.target.value }))}
              />
            </div>
            <div className="wsm_field wsm_field_wide">
              <label>Label (optional)</label>
              <input
                type="text"
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                placeholder="e.g. SalesGod Support #1"
              />
            </div>
          </div>

          <button
            className="wsm_submit_btn"
            onClick={handleAdd}
            disabled={saving}
          >
            {saving ? 'Adding...' : 'Add to Warmup Network'}
          </button>
        </div>
      )}

      <div className="wsm_seed_list">
        {loading ? (
          <div className="wsm_loading">Loading seed accounts...</div>
        ) : seeds.length === 0 ? (
          <div className="wsm_empty">
            <div className="wsm_empty_icon">📭</div>
            <p>No seed accounts yet. Add your first one to bootstrap the warmup network.</p>
          </div>
        ) : (
          seeds.map(seed => {
            const lastError = seed.lastError;
            const lastSuccessAt = seed.lastSuccessAt;
            const errorCount24h = seed.errorCount24h || 0;
            const isStale = !!lastError && (!lastSuccessAt || lastError.at > lastSuccessAt);
            const isErrorExpanded = expandedErrorEmail === seed.email;
            return (
              <div key={seed.id} className={`wsm_seed_card_wrap ${isStale ? 'wsm_seed_card_wrap_error' : ''}`}>
                <div className={`wsm_seed_card ${seed.active ? '' : 'wsm_seed_inactive'} ${isStale ? 'wsm_seed_card_error' : ''} ${seed.quarantined ? 'wsm_seed_card_paused' : ''}`}>
                  <div className="wsm_seed_info">
                    <div className="wsm_seed_email">
                      {seed.email}
                      {isStale && <span className="wsm_seed_error_dot" title={lastError.message} />}
                      {seed.quarantined && (
                        <span className="wsm_seed_paused_badge" title={seed.quarantineReason === 'errors' ? 'Auto-paused after repeated send failures. Resumes on next successful send.' : 'Quarantined for high bounce/complaint rate.'}>
                          ⏸ Paused {seed.quarantineReason === 'errors' ? '· connectivity' : '· deliverability'}
                        </span>
                      )}
                    </div>
                    {seed.label && <div className="wsm_seed_label">{seed.label}</div>}
                    <div className="wsm_seed_meta">
                      Added {seed.createdAt ? new Date(seed.createdAt).toLocaleDateString() : 'recently'}
                      {lastSuccessAt && (
                        <span className="wsm_seed_meta_ok"> · Last successful send {timeAgoShort(lastSuccessAt)}</span>
                      )}
                    </div>
                  </div>
                  <div className="wsm_seed_actions">
                    <div
                      className={`wsm_toggle ${seed.active ? 'wsm_toggle_on' : ''} ${togglingEmail === seed.email ? 'wsm_toggle_busy' : ''}`}
                      onClick={() => handleToggle(seed.email, seed.active)}
                    >
                      <div className="wsm_toggle_thumb" />
                    </div>
                    <button
                      className="wsm_remove_btn"
                      onClick={() => handleRemove(seed.email)}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </div>
                </div>
                {isStale && lastError && (
                  <div className="wsm_error_banner" onClick={() => setExpandedErrorEmail(isErrorExpanded ? null : seed.email)}>
                    <div className="wsm_error_main">
                      <div className="wsm_error_icon">!</div>
                      <div className="wsm_error_text">
                        <div className="wsm_error_title">
                          Backend error
                          {errorCount24h > 1 && <span className="wsm_error_count">{errorCount24h} in 24h</span>}
                        </div>
                        <div className="wsm_error_message">{lastError.message}</div>
                        <div className="wsm_error_meta">
                          <span>Type: <strong>{lastError.type}</strong></span>
                          {lastError.peer && <span>Peer: <strong>{lastError.peer}</strong></span>}
                          <span>{timeAgoShort(lastError.at)}</span>
                        </div>
                      </div>
                      <span className="wsm_error_chevron">{isErrorExpanded ? '▲' : '▼'}</span>
                    </div>
                    {isErrorExpanded && Array.isArray(seed.recentErrors) && seed.recentErrors.length > 0 && (
                      <div className="wsm_error_log">
                        <div className="wsm_error_log_title">Recent failures</div>
                        {seed.recentErrors.map((e, idx) => (
                          <div key={idx} className="wsm_error_log_row">
                            <div className="wsm_error_log_top">
                              <span className="wsm_error_log_type">{e.type}</span>
                              <span className="wsm_error_log_time">{timeAgoShort(e.at)}</span>
                            </div>
                            <div className="wsm_error_log_msg">{e.message}</div>
                            {e.peer && <div className="wsm_error_log_peer">→ {e.peer}</div>}
                            {e.raw && e.raw !== e.message && (
                              <div className="wsm_error_log_raw">{e.raw}</div>
                            )}
                          </div>
                        ))}
                        <button
                          className="wsm_error_refresh"
                          onClick={(ev) => { ev.stopPropagation(); fetchSeeds(); }}
                        >
                          Refresh
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="wsm_info_box">
        <strong>How it works:</strong> Seed accounts are automatically included
        as warmup peers for all users. The scheduled processor sends and engages
        warmup emails through these accounts every 15 minutes — just like real
        user accounts, but admin-managed and invisible to end users.
      </div>
    </div>
  );
}

export default WarmupSeedManager;
